import { isFuture } from 'date-fns';
import { parse, toSeconds } from 'iso8601-duration';
import { createDraftSafeSelector } from '@reduxjs/toolkit';

import { Brand } from 'types/Brand';
import { RootState } from 'App/Store';
import { PoliciesEnum } from 'modules/auth/types/Policies';
import type InitialState from 'modules/dealers/types/InitialState';
import { ErrorType } from 'components/Home/KioskWarningModal/types';
import { KioskType, SHAREBOX_INDOOR_KIOSK_MODELS, ShareboxModel } from 'types/KioskTypes';
import {
  AllSelfServiceTypes,
  ConfiguredWorkflows,
  SelfServiceOrigin,
  SelfServiceType
} from 'modules/selfServices/types/SelfService';

import { uniq } from 'utils/objectUtils';
import { getUrlParam } from 'utils/urlUtils';
import { getLabelTranslation } from 'utils/i18nUtils';

import { getSelectedLanguage } from 'modules/translations/selectors';

import { HelpLinePhoneNumber } from './types/HelpLinePhoneNumber';
import { ConnectionTypeLogin, ContextResponse, Dealer, KioskConnectionStatus } from './types/ContextResponse';

import { DEFAULT_SCREENSAVER_PERIOD } from 'modules/kiosk/constants';

import { formatPhoneNumber } from '../communication/utils';
import merge from 'deepmerge';
import { mapDealerIdsToNames } from './utils';

const getState = (state: RootState) => state.dealers;

const getProps = (_: RootState, props: string) => props;

export const getContext = createDraftSafeSelector(
  getState,
  ({ context }: InitialState) => context,
);
export const getOrigin = createDraftSafeSelector(
  getContext,
  (context) => context?.origin,
);

export const getKioskName = createDraftSafeSelector(
  getContext,
  (context) => context?.kioskName,
);

export const getKioskTypes = createDraftSafeSelector(
  getContext,
  (context) => context?.kioskTypes ?? [],
);

export const getKioskId = createDraftSafeSelector(
  getContext,
  (context) => context?.kioskId,
);

export const getKioskType = createDraftSafeSelector(
  getContext,
  (context) => context?.kioskType,
);

export const getKioskModel = createDraftSafeSelector(
  getContext,
  (context) => context?.kioskModel,
);

export const getKioskImages = createDraftSafeSelector(
  getContext,
  (context) => context?.kioskImages,
);

export const getDealerCustomerLanguage = createDraftSafeSelector(
  getContext,
  (context) => context?.dealerCustomerLanguage,
);

export const getContextSelfServiceType = createDraftSafeSelector(
  getContext,
  (context) => context?.type,
);

export const getLoginToken = createDraftSafeSelector(
  getContext,
  (context) => context?.token,
);

export const getConnectionType = createDraftSafeSelector(
  getContext,
  (context) => context?.connectionTypes,
);

export const isCodeLoginEnabled = createDraftSafeSelector(
  getConnectionType,
  (connectionTypes) => connectionTypes?.includes(ConnectionTypeLogin.CODE) ?? false,
);

export const getScreensaverPeriod = createDraftSafeSelector(
  getContext,
  (context) => toSeconds(parse(context?.kioskScreensaver?.timeConfiguration ?? DEFAULT_SCREENSAVER_PERIOD)),
);

export const getScreensaverMedia = createDraftSafeSelector(
  getContext,
  (context) => context?.kioskScreensaver?.medias ?? [],
);

export const isKioskOrigin = createDraftSafeSelector(
  getContext,
  (context) => context?.origin === SelfServiceOrigin.KIOSK,
);

/** @description the user is using the kiosk on its screen */
export const isLocalKiosk = createDraftSafeSelector(
  isKioskOrigin,
  getKioskType,
  getKioskModel,
  getConnectionType,
  (isKiosk, kioskType, model, connectionTypes) => {
    let isLocal = isKiosk;

    if (isKiosk) {
      // KIOSK + MFA is local most of the time...
      isLocal = connectionTypes?.includes(ConnectionTypeLogin.MFA);
      // kioskType-specific rules
      if (isLocal && kioskType === KioskType.SHAREBOX) {
        // ...Except for Sharebox OUTDOOR kiosk models
        isLocal = SHAREBOX_INDOOR_KIOSK_MODELS.includes(model as ShareboxModel);
      }
    }

    return isLocal;
  },
);

/** @description the user is using the kiosk on their device (i.e NOT using device's screen) */
export const isRemoteKiosk = createDraftSafeSelector(
  isKioskOrigin,
  getKioskType,
  getKioskModel,
  getConnectionType,
  (isKiosk, kioskType, model, connectionTypes) => {
    let isRemote = isKiosk;

    if (isKiosk) {
      // KIOSK + TOKEN auth is necessarily remote, regardless of the kioskType. For the moment.
      isRemote = connectionTypes?.includes(ConnectionTypeLogin.TOKEN);
      // kioskType-specific rules
      if (!isRemote) {
        if (kioskType === KioskType.SHAREBOX) {
          // KIOSK + MFA is remote for Sharebox OUTDOOR kiosk models
          isRemote = connectionTypes?.includes(ConnectionTypeLogin.MFA)
                    && !SHAREBOX_INDOOR_KIOSK_MODELS.includes(model as ShareboxModel);
        }
      }
    }
    return isRemote;
  },
);

/** @see {@link SHAREBOX_INDOOR_KIOSK_MODELS} */
export const hasShareboxStyle = createDraftSafeSelector(
  isLocalKiosk,
  getKioskType,
  getKioskModel,
  (isLocal, kioskType, model) => (
    isLocal && kioskType === KioskType.SHAREBOX && SHAREBOX_INDOOR_KIOSK_MODELS.includes(model as ShareboxModel)
  ),
);

export const isPublicOnSite = createDraftSafeSelector(
  getContext,
  (context) => context?.isPublic && context?.origin === SelfServiceOrigin.ON_SITE,
);

export const isPublicDevice = createDraftSafeSelector(
  getContext,
  isLocalKiosk,
  (context, localKiosk) => {
    const outdoorShareboxToken = getUrlParam('OSToken');
    return context?.isPublic || localKiosk || Boolean(outdoorShareboxToken);
  },
);

export const getSelfServicePhoneNumber = createDraftSafeSelector(
  getContext,
  (context) => context?.selfServicePhoneNumber,
);

export const getFormattedSelfServicePhoneNumber = createDraftSafeSelector(
  getSelfServicePhoneNumber,
  (phoneNumber) => formatPhoneNumber(phoneNumber),
);

export const getKioskDealers = createDraftSafeSelector(
  getContext,
  (context: ContextResponse) => context?.kioskDealers ?? [],
);

export const getKioskDealersWithBreakdown = createDraftSafeSelector(
  getOrigin,
  getKioskDealers,
  (origin, dealers) => dealers.filter(
    (dealer: Dealer) => dealer.selfServiceAvailableTypes[SelfServiceType.BREAKDOWN]?.includes(origin),
  ),
);

export const getBrands = createDraftSafeSelector(getContext, (context) => context?.brands);

export const getDealerId = createDraftSafeSelector(
  getContext,
  getKioskDealersWithBreakdown,
  (context, kioskDealers) => context?.id ?? kioskDealers?.[0]?.id,
);

export const getBrandsByDealerId = createDraftSafeSelector(
  getKioskDealersWithBreakdown,
  getBrands,
  getProps,
  (kioskDealers, brands, dealerId) => kioskDealers?.find(({ id }: Dealer) => id === dealerId)?.brands ?? brands,
);

export const getDefaultBrandId = createDraftSafeSelector(
  getBrandsByDealerId,
  (brands) => brands?.[0]?.id,
);

export const getPrefix = createDraftSafeSelector(
  getContext,
  (context) => context?.dealerCountry?.prefix,
);

export const getPrefixByDealerId = createDraftSafeSelector(
  getKioskDealersWithBreakdown,
  getPrefix,
  getProps,
  (kioskDealers, prefix, dealerId) => prefix ?? kioskDealers?.find(
    ({ id }: Dealer) => id === dealerId,
  )?.dealerCountry?.prefix,
);

export const getFormattedBrands = createDraftSafeSelector(
  getBrandsByDealerId,
  (brands: Array<Brand>) => brands?.reduce<Record<string, string>>(
    (acc, { name, id }) => ({ ...acc, [id]: name }),
    {},
  ),
);

export const getFormattedDealers = createDraftSafeSelector(
  getKioskDealersWithBreakdown,
  (dealers: Array<Dealer>) => mapDealerIdsToNames(dealers),
);

export const getHelpLinePhoneNumbers = createDraftSafeSelector(getContext, (context: ContextResponse) => {
  if (context?.selfServicePhoneNumber) {
    return [{ selfServicePhoneNumber: context.selfServicePhoneNumber }] as HelpLinePhoneNumber[];
  }

  if (!context?.dealerName && context?.kioskDealers) {
    return Object.values(
      context.kioskDealers.reduce((acc, dealer) => {
        if (dealer?.selfServicePhoneNumber) {
          acc[dealer.id] = { dealerName: dealer.dealerName, selfServicePhoneNumber: dealer.selfServicePhoneNumber };
        }
        return acc;
      }, {} as Record<string, HelpLinePhoneNumber>),
    );
  }

  return [];
});

export const shouldDisplayPhoneNumbers = createDraftSafeSelector(
  isPublicDevice,
  getHelpLinePhoneNumbers,
  (isPublic, phoneNumbers) => isPublic && phoneNumbers.length > 0,
);

export const isKioskFull = createDraftSafeSelector(
  getContext,
  (context) => context?.origin === SelfServiceOrigin.KIOSK && !context.isAvailable,
);

export const getConfidentialityPolicyLink = createDraftSafeSelector(
  getContext,
  getSelectedLanguage,
  (context, language) => {
    const confidentialityPolicyFromDealer = context?.kioskDealers?.length === 1 && context?.kioskDealers?.at(0).confidentialityPolicyLink;
    const confidentialityPolicy = context?.confidentialityPolicyLink ?? confidentialityPolicyFromDealer;
    return getLabelTranslation(confidentialityPolicy, language);},
);

export const getGeneralTermsConditionsLink = createDraftSafeSelector(
  getContext,
  getSelectedLanguage,
  ({ generalTermsConditionsLink }, language) => getLabelTranslation(generalTermsConditionsLink, language),
);

export const getPolicies = createDraftSafeSelector(
  getGeneralTermsConditionsLink,
  getConfidentialityPolicyLink,
  (generalTermsConditions, confidentialityPolicy) => [
    ...(generalTermsConditions ? [{ link: generalTermsConditions, type: PoliciesEnum.GENERAL_TERMS_CONDITION }] : []),
    ...(confidentialityPolicy ? [{ link: confidentialityPolicy, type: PoliciesEnum.CONFIDENTIALITY_POLICY }] : []),
  ],
);

export const isKioskReadyOptional = createDraftSafeSelector(getKioskType, (kioskType) => kioskType === KioskType.SHAREBOX);

export const isKioskEmergencyMode = createDraftSafeSelector(getContext, (context) => context?.isEmergencyMode);

const isKioskOffline = createDraftSafeSelector(
  getContext,
  (context) => context?.kioskConnectionStatus === KioskConnectionStatus.OFFLINE,
);

export const hasErrorOnKiosk = createDraftSafeSelector(
  isKioskOffline,
  isKioskEmergencyMode,
  (...args) => args.some(Boolean),
);

export const getCourtesyVehicleAvailabilityDate = createDraftSafeSelector(
  getContext,
  (context) => context?.courtesyVehicleAvailabilityDate,
);

export const isCourtesyUnavailable = createDraftSafeSelector(
  getCourtesyVehicleAvailabilityDate,
  (courtesyVehicleAvailabilityDate) => isFuture(courtesyVehicleAvailabilityDate),
);

const transformDealerIdsToArray = createDraftSafeSelector(
  getProps,
  (dealerIds) => dealerIds?.split(',') ?? [],
);

const getFirstDealerByIds = createDraftSafeSelector(
  getKioskDealers,
  transformDealerIdsToArray,
  (dealers, idsArray) => dealers?.find(({ id }) => idsArray.includes(id)),
);

export const getTimezoneByDealerId = createDraftSafeSelector(
  getFirstDealerByIds,
  (dealer) => dealer?.timezone,
);

export const displayOriginSelection = createDraftSafeSelector(
  getOrigin,
  getKioskTypes,
  getKioskId,
  getContextSelfServiceType,
  (origin, availableKioskTypes, kioskId, type) => (
    origin === SelfServiceOrigin.HOME
    && availableKioskTypes.includes(KioskType.SHAREBOX)
    // For the check-out, if the locationId is undefined it means that keys are not in the kiosk
    && (type === SelfServiceType.CHECK_IN || Boolean(kioskId))
  ),
);

export const getSelfServiceAvailableTypes = createDraftSafeSelector(
  getContext,
  getKioskDealers,
  (context, dealers): Partial<Record<SelfServiceType, SelfServiceOrigin[]>> => {
    if (context?.kioskId) {
      return AllSelfServiceTypes.reduce((acc, type) => ({
        ...acc,
        [type]: uniq(dealers.flatMap((dealer) => dealer.selfServiceAvailableTypes?.[type] ?? [])),
      }), {});
    }
    return context?.selfServiceAvailableTypes ?? {};
  },
);

export const isBreakdownAvailable = createDraftSafeSelector(
  getSelfServiceAvailableTypes,
  getOrigin,
  (availableTypes, origin) => availableTypes[SelfServiceType.BREAKDOWN]?.includes(origin) ?? false,
);

/**
 * Intended to be used in the error page to display the correct message, hence the fallback on the 404 status code.
 */
export const getContextRequestStatus = createDraftSafeSelector(
  getState,
  ({ status }) => status,
);

export const getDealerName = createDraftSafeSelector(
  getContext,
  (context) => context?.dealerName,
);

export const isContextExpired = createDraftSafeSelector(
  getContext,
  (context) => context?.isTokenExpired,
);

export const getPaymentMethods = createDraftSafeSelector(
  getContext,
  (context) => context?.paymentMethods?.methods,
);

export const getKioskWarningErrorType = createDraftSafeSelector(
  hasErrorOnKiosk,
  isKioskFull,
  isCourtesyUnavailable,
  (hasError, isFull, isCourtesyNotAvailable) => {
    // Condition order matters
    if (hasError) {
      return ErrorType.KIOSK_ERROR;
    }
    if (isCourtesyNotAvailable) {
      return ErrorType.COURTESY_UNAVAILABLE;
    }
    if (isFull) {
      return ErrorType.KIOSK_FULL;
    }
    return undefined;
  },
);

export const hasScreensaver = createDraftSafeSelector(
  isLocalKiosk,
  getKioskType,
  getKioskModel,
  getScreensaverMedia,
  (isKiosk, kioskType, model, media) => isKiosk && media?.length > 0 && (
    kioskType === KioskType.PARTTEAM ||
    (kioskType === KioskType.SHAREBOX && SHAREBOX_INDOOR_KIOSK_MODELS.includes(model as ShareboxModel))
  )
);

export const getWorkflowsForAllDealers = createDraftSafeSelector(
  getKioskDealers,
  (dealers) => dealers.reduce((acc, dealer) => merge(acc, dealer.workflows ?? {}), {} as ConfiguredWorkflows)
);
